import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { authApi } from '../../api/facades';
import { useAuth } from '../../context/AuthContext';

const ChangePasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const { refreshUser, logout } = useAuth();

  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPw.length < 8) {
      toast.error('New password must be at least 8 characters.');
      return;
    }
    if (newPw !== confirmPw) {
      toast.error('New passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      await authApi.changePassword({
        current_password: currentPw,
        new_password: newPw,
        new_password_confirmation: confirmPw,
      });
      await refreshUser();
      toast.success('Password set. Welcome to ClickLaw!');
      navigate('/dashboard', { replace: true });
    } catch (err: any) {
      const msg =
        err?.body?.errors?.current_password?.[0] ||
        err?.body?.message ||
        err?.message ||
        'Failed to update password.';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900 px-4">
      <div className="w-full max-w-md bg-white dark:bg-slate-800 rounded-2xl shadow-lg p-8">
        <div className="mb-6">
          <h1 className="text-xl font-semibold text-slate-800 dark:text-slate-100 mb-1">
            Set your new password
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            You're logging in with a temporary password. Please set a new password to continue.
          </p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1">
              Temporary password
            </label>
            <input
              type="password"
              autoComplete="current-password"
              className="w-full rounded-lg bg-gray-50 py-2 px-3 text-sm text-slate-700 focus:outline-none dark:bg-slate-900 dark:text-slate-200 border border-slate-200 dark:border-slate-700"
              value={currentPw}
              onChange={(e) => setCurrentPw(e.target.value)}
              required
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1">
              New password
            </label>
            <input
              type="password"
              autoComplete="new-password"
              className="w-full rounded-lg bg-gray-50 py-2 px-3 text-sm text-slate-700 focus:outline-none dark:bg-slate-900 dark:text-slate-200 border border-slate-200 dark:border-slate-700"
              value={newPw}
              onChange={(e) => setNewPw(e.target.value)}
              required
              minLength={8}
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1">
              Confirm new password
            </label>
            <input
              type="password"
              autoComplete="new-password"
              className="w-full rounded-lg bg-gray-50 py-2 px-3 text-sm text-slate-700 focus:outline-none dark:bg-slate-900 dark:text-slate-200 border border-slate-200 dark:border-slate-700"
              value={confirmPw}
              onChange={(e) => setConfirmPw(e.target.value)}
              required
              minLength={8}
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full py-2.5 mt-2 rounded-lg bg-primary text-white text-sm font-semibold hover:bg-primary/90 disabled:opacity-60"
          >
            {loading ? 'Saving...' : 'Set new password'}
          </button>
        </form>
        <button
          type="button"
          onClick={() => logout()}
          className="mt-4 w-full text-center text-xs text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
        >
          Sign out
        </button>
      </div>
    </div>
  );
};

export default ChangePasswordPage;
